import type { ConfirmModalProps } from "@/components/confirm-modal/ConfirmModal";
import ConfirmModal from "@/components/confirm-modal/ConfirmModal";
import type { ModalBaseProps } from "@/stores/application";
import useApplicationStore from "@/stores/application/application.store";
import React from "react";
import { v4 as uuidv4 } from "uuid";
import ModalWrapper from "./components/modal-wrapper";

const ModalProvider = () => {
  const { modals } = useApplicationStore();
  return (
    <>
      {modals.map((modalInstance) => (
        <ModalWrapper key={modalInstance.id} modalInstance={modalInstance} />
      ))}
    </>
  );
};

export default React.memo(ModalProvider);

export const openModal = <P extends ModalBaseProps>(Component: React.ComponentType<P>, props: P) => {
  const { modals, setModals } = useApplicationStore.getState();
  setModals([...modals, { Component, props, id: uuidv4() }]);
};

export const openConfirmInitDataModal = (onSubmit: ModalBaseProps["onSubmit"], loading?: boolean) => {
  const { setIsDisabledKeyboardShortcut } = useApplicationStore.getState();
  setIsDisabledKeyboardShortcut(true);
  openModal(ConfirmModal, {
    title: "Bạn có chắc chắn muốn khởi tạo dữ liệu?",
    description: "Hành động này sẽ không thể hoàn tác.",
    confirmLabel: "Khởi tạo",
    onSubmit,
    loading,
    onClose: () => setIsDisabledKeyboardShortcut(false),
  });
};

export const openConfirmDeleteModal = (onSubmit: ModalBaseProps["onSubmit"], loading?: boolean) => {
  const { setIsDisabledKeyboardShortcut } = useApplicationStore.getState();
  setIsDisabledKeyboardShortcut(true);
  openModal(ConfirmModal, {
    title: "Bạn có chắc chắn muốn xóa dữ liệu này?",
    description: "Hành động này sẽ không thể hoàn tác.",
    confirmLabel: "Xóa",
    onSubmit,
    loading,
    onClose: () => setIsDisabledKeyboardShortcut(false),
  });
};

export const openConfirmFollowModal = (is_followed: boolean, onSubmit: ModalBaseProps["onSubmit"]) => {
  const { setIsDisabledKeyboardShortcut } = useApplicationStore.getState();
  setIsDisabledKeyboardShortcut(true);
  openModal(ConfirmModal, {
    title: is_followed ? "Bạn có chắc chắn muốn hủy theo dõi?" : "Bạn có chắc chắn muốn theo dõi?",
    confirmLabel: is_followed ? "Hủy theo dõi" : "Theo dõi",
    onSubmit,
    onClose: () => setIsDisabledKeyboardShortcut(false),
  });
};

export const openWarningCloseForm = (onSubmit: ModalBaseProps["onSubmit"]) => {
  const { setIsDisabledKeyboardShortcut } = useApplicationStore.getState();
  setIsDisabledKeyboardShortcut(true);
  openModal(ConfirmModal, {
    title: "Bạn có chắc muốn thoát?",
    description: "Dữ liệu chưa được lưu, bạn có chắc chắn muốn thoát, hành động này không thể được hoàn tác.",
    confirmLabel: "Thoát",
    cancelLabel: "Hủy bỏ",
    onSubmit,
    onClose: () => setIsDisabledKeyboardShortcut(false),
  });
};

export const openConfirmRecordModal = (onSubmit: ModalBaseProps["onSubmit"], loading?: boolean) => {
  const { setIsDisabledKeyboardShortcut } = useApplicationStore.getState();
  setIsDisabledKeyboardShortcut(true);
  openModal(ConfirmModal, {
    title: "Bạn có chắc chắn muốn ghi sổ hàng loạt không?",
    description: "Hành động này sẽ không thể hoàn tác.",
    confirmLabel: "Ghi sổ",
    onSubmit,
    loading,
    onClose: () => setIsDisabledKeyboardShortcut(false),
  });
};

export const openConfirmQuitRecordModal = (onSubmit: ModalBaseProps["onSubmit"], loading?: boolean) => {
  const { setIsDisabledKeyboardShortcut } = useApplicationStore.getState();
  setIsDisabledKeyboardShortcut(true);
  openModal(ConfirmModal, {
    title: "Bạn có chắc chắn muốn bỏ ghi sổ hàng loạt không?",
    description: "Hành động này sẽ không thể hoàn tác.",
    confirmLabel: "Bỏ ghi sổ",
    onSubmit,
    loading,
    onClose: () => setIsDisabledKeyboardShortcut(false),
  });
};

export const openConfirmModal = (props: ConfirmModalProps) => {
  const { setIsDisabledKeyboardShortcut } = useApplicationStore.getState();
  setIsDisabledKeyboardShortcut(true);
  openModal(ConfirmModal, {
    ...props,
    onClose: () => {
      setIsDisabledKeyboardShortcut(false);
      props.onClose?.();
    },
  });
};
